import { AxiosResponse } from "axios";
import { http } from "../http";

export type CouponType = "PERCENTAGE" | "FIXED";
export type CouponScope = "ALL" | "SERVICE" | "CLINICIAN";
export type CouponDisplayStatus =
  | "ACTIVE"
  | "INACTIVE"
  | "EXPIRED"
  | "SCHEDULED";

export interface CouponAssignment {
  id: number;
  patient_id: number;
  created_at: string;
  patient?: {
    id: number;
    users?: {
      id: number;
      full_name?: string;
      email?: string;
      mobile_number?: string;
    };
  };
}

export interface Coupon {
  id: number;
  code: string;
  title_en: string;
  title_ar?: string | null;
  description_en?: string | null;
  description_ar?: string | null;
  type: CouponType;
  value: number;
  min_order_amount?: number | null;
  max_discount_amount?: number | null;
  usage_limit?: number | null;
  usage_per_user?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  is_active: boolean;
  is_public: boolean;
  scope: CouponScope;
  service_ids: number[];
  clinician_ids: number[];
  created_at: string;
  updated_at: string;
  usage_count?: number;
  assignment_count?: number;
  display_status?: CouponDisplayStatus;
  assignments?: CouponAssignment[];
}

export interface CouponFilters {
  search?: string;
  is_active?: boolean;
  is_public?: boolean;
  type?: CouponType;
  limit?: number;
  offset?: number;
}

export interface CreateCouponPayload {
  code: string;
  title_en: string;
  title_ar?: string;
  description_en?: string;
  description_ar?: string;
  type: CouponType;
  value: number;
  min_order_amount?: number | null;
  max_discount_amount?: number | null;
  usage_limit?: number | null;
  usage_per_user?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  is_active?: boolean;
  is_public?: boolean;
  scope?: CouponScope;
  service_ids?: number[];
  clinician_ids?: number[];
}

export type UpdateCouponPayload = Partial<CreateCouponPayload>;

export interface CouponListResponse {
  status: string;
  data: Coupon[];
  meta: { total: number; limit: number; offset: number };
}

export interface CouponResponse {
  status: string;
  message?: string;
  data: Coupon;
}

/**
 * Server CamelCaseResponseInterceptor turns snake_case → camelCase.
 * Admin forms/tables expect snake_case — normalize both shapes.
 */
const pick = <T>(raw: any, snake: string, camel: string, fallback?: T): T => {
  if (raw == null) return fallback as T;
  if (raw[snake] !== undefined && raw[snake] !== null) return raw[snake] as T;
  if (raw[camel] !== undefined && raw[camel] !== null) return raw[camel] as T;
  return fallback as T;
};

const normalizeUsers = (users: any) => {
  if (!users) return undefined;
  return {
    id: users.id,
    full_name: pick<string | undefined>(users, "full_name", "fullName"),
    email: users.email,
    mobile_number: pick<string | undefined>(
      users,
      "mobile_number",
      "mobileNumber"
    ),
  };
};

const normalizeAssignment = (a: any): CouponAssignment => ({
  id: a.id,
  patient_id: pick<number>(a, "patient_id", "patientId"),
  created_at: pick<string>(a, "created_at", "createdAt"),
  patient: a.patient
    ? {
        id: a.patient.id,
        users: normalizeUsers(a.patient.users),
      }
    : undefined,
});

export const normalizeCoupon = (raw: any): Coupon => {
  if (!raw) return raw;
  return {
    id: raw.id,
    code: raw.code,
    title_en: pick(raw, "title_en", "titleEn", ""),
    title_ar: pick(raw, "title_ar", "titleAr", null),
    description_en: pick(raw, "description_en", "descriptionEn", null),
    description_ar: pick(raw, "description_ar", "descriptionAr", null),
    type: raw.type,
    value: Number(raw.value),
    min_order_amount: pick(raw, "min_order_amount", "minOrderAmount", null),
    max_discount_amount: pick(
      raw,
      "max_discount_amount",
      "maxDiscountAmount",
      null
    ),
    usage_limit: pick(raw, "usage_limit", "usageLimit", null),
    usage_per_user: pick(raw, "usage_per_user", "usagePerUser", null),
    starts_at: pick(raw, "starts_at", "startsAt", null),
    ends_at: pick(raw, "ends_at", "endsAt", null),
    is_active: pick(raw, "is_active", "isActive", true),
    is_public: pick(raw, "is_public", "isPublic", true),
    scope: pick(raw, "scope", "scope", "ALL"),
    service_ids: pick(raw, "service_ids", "serviceIds", []) || [],
    clinician_ids: pick(raw, "clinician_ids", "clinicianIds", []) || [],
    created_at: pick(raw, "created_at", "createdAt", ""),
    updated_at: pick(raw, "updated_at", "updatedAt", ""),
    usage_count: pick(raw, "usage_count", "usageCount", 0),
    assignment_count: pick(raw, "assignment_count", "assignmentCount", 0),
    display_status: pick(raw, "display_status", "displayStatus", "ACTIVE"),
    assignments: Array.isArray(raw.assignments)
      ? raw.assignments.map(normalizeAssignment)
      : undefined,
  };
};

const normalizeListResponse = (
  res: AxiosResponse<any>
): AxiosResponse<CouponListResponse> => {
  const body = res.data;
  const list = body?.data;
  if (Array.isArray(list)) {
    res.data = {
      ...body,
      data: list.map(normalizeCoupon),
      meta: body.meta || {
        total: list.length,
        limit: list.length,
        offset: 0,
      },
    };
  }
  return res;
};

const normalizeOneResponse = (
  res: AxiosResponse<any>
): AxiosResponse<CouponResponse> => {
  if (res.data?.data) {
    res.data = {
      ...res.data,
      data: normalizeCoupon(res.data.data),
    };
  }
  return res;
};

export const couponService = {
  list: (filters: CouponFilters = {}) =>
    http
      .get<CouponListResponse>("/admin/coupons", { params: filters })
      .then(normalizeListResponse),

  getById: (id: number) =>
    http.get<CouponResponse>(`/admin/coupons/${id}`).then(normalizeOneResponse),

  create: (data: CreateCouponPayload) =>
    http.post<CouponResponse>("/admin/coupons", data).then(normalizeOneResponse),

  update: (id: number, data: UpdateCouponPayload) =>
    http
      .patch<CouponResponse>(`/admin/coupons/${id}`, data)
      .then(normalizeOneResponse),

  remove: (id: number) => http.delete(`/admin/coupons/${id}`),

  assignPatients: (id: number, patientIds: number[]) =>
    http.post(`/admin/coupons/${id}/assignments`, { patientIds }),

  unassignPatient: (id: number, patientId: number) =>
    http.delete(`/admin/coupons/${id}/assignments/${patientId}`),

  grantFreeSessions: (data: {
    patientId: number;
    count: number;
    title_en?: string;
    title_ar?: string;
    ends_at?: string | null;
    clinicianIds?: number[];
  }) =>
    http
      .post<CouponResponse>("/admin/coupons/grant-free-sessions", data)
      .then(normalizeOneResponse),
};
